import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Search, X } from "lucide-react";
import { EMOTIONS, COMO_TRANSFORMAR, EMOCOES_PAGINA_11 } from "@/data/emotions";
import { getOrgan } from "@/data/organs";
import { elementColor } from "@/data/theme";
import { PageHeader, SafetyNote, SourceTag, ElementBadge } from "@/components/common";

const normalizeText = (str) =>
  (str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const getElementKey = (el) => {
  if (!el) return null;
  return el.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

function matchEmotion(em, query) {
  if (!query) return true;
  const normQuery = normalizeText(query);
  const emocaoText = normalizeText(em.emocao);
  const aliasesText = normalizeText((em.aliases || []).join(" "));
  const fullTarget = `${emocaoText} ${aliasesText}`;
  return fullTarget.includes(normQuery);
}

export default function Emotions() {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredEmotions = searchTerm
    ? EMOTIONS.filter((em) => matchEmotion(em, searchTerm))
    : EMOTIONS;

  return (
    <div className="space-y-12">
      {/* PÁGINA FÍSICA 11 / IMPRESSA 9: EMOÇÕES E ÓRGÃOS */}
      <section className="space-y-8" data-testid="section-emocoes-pagina-11">
        <PageHeader
          eyebrow="Visão emocional da MTC"
          title={EMOCOES_PAGINA_11.titulo}
          subtitle={EMOCOES_PAGINA_11.subtitulo}
        >
          <div className="mt-3">
            <SourceTag source={EMOCOES_PAGINA_11.source} />
          </div>
        </PageHeader>

        {/* Texto introdutório e Citação */}
        <div className="grid gap-6 lg:grid-cols-12 items-start">
          <div className="lg:col-span-7">
            <p className="font-serif text-lg text-foreground font-medium leading-relaxed">
              {EMOCOES_PAGINA_11.intro}
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-gold/30 bg-gold/5 p-6 text-center">
              <p className="font-serif text-base sm:text-lg font-medium italic text-foreground">
                “{EMOCOES_PAGINA_11.citacao.linha1}”
              </p>
              <p className="font-serif text-base sm:text-lg font-medium italic text-gold mt-1">
                “{EMOCOES_PAGINA_11.citacao.linha2}”
              </p>
            </div>
          </div>
        </div>

        {/* Como funciona essa conexão? + A linguagem do corpo VS Lembre-se */}
        <div className="grid gap-6 lg:grid-cols-12 items-start">
          {/* Coluna esquerda: Como funciona & Linguagem do corpo */}
          <div className="lg:col-span-7 space-y-6">
            {/* Como funciona essa conexão? */}
            <div className="rounded-3xl border border-border bg-card p-6 sm:p-7 space-y-4" data-testid="box-como-funciona-conexao">
              <h3 className="font-serif text-xl font-semibold text-foreground flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-gold" />
                {EMOCOES_PAGINA_11.comoFunciona.titulo}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {EMOCOES_PAGINA_11.comoFunciona.texto1}
              </p>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                {EMOCOES_PAGINA_11.comoFunciona.texto2}
              </p>
            </div>

            {/* A linguagem do corpo */}
            <div className="rounded-2xl border border-border/80 bg-secondary/40 p-6 space-y-2" data-testid="box-linguagem-do-corpo">
              <h4 className="font-serif text-base font-semibold text-foreground">
                {EMOCOES_PAGINA_11.linguagemDoCorpo.titulo}
              </h4>
              <p className="text-sm text-foreground/90 font-medium">
                {EMOCOES_PAGINA_11.linguagemDoCorpo.texto1}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {EMOCOES_PAGINA_11.linguagemDoCorpo.texto2}
              </p>
            </div>
          </div>

          {/* Coluna direita: Lembre-se */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-border bg-card p-6 sm:p-7 space-y-4" data-testid="box-lembre-se-emocoes">
              <h3 className="font-serif text-xl font-semibold text-foreground flex items-center gap-2">
                <Heart className="h-5 w-5 text-gold" />
                {EMOCOES_PAGINA_11.lembreSe.titulo}
              </h3>
              <div className="space-y-3">
                {EMOCOES_PAGINA_11.lembreSe.pontos.map((ponto, i) => (
                  <div key={i} className="flex items-start gap-3 rounded-2xl bg-secondary/40 border border-border/60 p-3.5">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold text-xs font-semibold mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-medium">
                      {ponto}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Banner inferior: Mensageiras + Importante */}
        <div className="grid gap-4 sm:grid-cols-2 rounded-3xl border border-border bg-card p-6 sm:p-7" data-testid="box-fechamento-emocoes">
          <div className="space-y-1 sm:border-r sm:border-border/60 sm:pr-6">
            <p className="font-serif text-sm sm:text-base font-semibold text-foreground uppercase tracking-wide">
              {EMOCOES_PAGINA_11.fechamento.destaque}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {EMOCOES_PAGINA_11.fechamento.subtexto}
            </p>
          </div>
          <div className="space-y-1 sm:pl-2 flex flex-col justify-center">
            <p className="text-xs font-semibold uppercase tracking-wider text-gold">
              Importante
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              {EMOCOES_PAGINA_11.fechamento.importante}
            </p>
          </div>
        </div>
      </section>

      {/* PÁGINA FÍSICA 12 / IMPRESSA 10: TABELA DE EMOÇÕES E ÓRGÃOS */}
      <section className="space-y-6 border-t border-border pt-10" data-testid="section-tabela-emocoes">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h2 className="text-2xl sm:text-3xl font-serif font-medium text-foreground">
              Tabela de Emoções e Órgãos
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              As sete emoções na visão tradicional da MTC e suas manifestações no corpo.
            </p>
          </div>
          <SourceTag source={{ pdfPage: 12, printedPage: "10" }} />
        </div>

        {/* Campo de busca de emoções */}
        <div className="relative w-full max-w-xl">
          <label htmlFor="emotion-search" className="sr-only">
            Buscar emoção ou estado relacionado
          </label>
          <div className="relative flex items-center">
            <Search className="absolute left-4 h-5 w-5 text-muted-foreground pointer-events-none" strokeWidth={1.75} />
            <input
              id="emotion-search"
              data-testid="emotion-search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Busque uma emoção ou estado (ex: irritabilidade, euforia, ruminação)..."
              aria-label="Busque uma emoção ou estado..."
              className="w-full rounded-2xl border border-input bg-card py-3.5 pl-11 pr-11 text-sm sm:text-base text-foreground placeholder:text-muted-foreground/70 outline-none transition-all focus:border-gold focus:ring-2 focus:ring-gold/25"
            />
            {searchTerm && (
              <button
                type="button"
                data-testid="clear-emotion-search"
                onClick={() => setSearchTerm("")}
                aria-label="Limpar busca"
                className="absolute right-3.5 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {searchTerm.trim() && (
            <p className="mt-2 text-xs text-muted-foreground px-1" data-testid="emotion-search-count">
              {filteredEmotions.length === 1
                ? "1 emoção encontrada"
                : `${filteredEmotions.length} emoções encontradas`}
            </p>
          )}
        </div>

        {filteredEmotions.length === 0 ? (
          <div
            data-testid="emotion-empty-state"
            className="rounded-3xl border border-dashed border-border bg-card/60 p-8 sm:p-12 text-center space-y-4"
          >
            <p className="text-base text-muted-foreground">
              Nenhuma emoção encontrada para esta busca.
            </p>
            <button
              type="button"
              data-testid="emotion-clear-button"
              onClick={() => setSearchTerm("")}
              className="inline-flex items-center rounded-full border border-gold/40 px-5 py-2 text-sm font-medium text-foreground hover:bg-secondary transition-colors"
            >
              Limpar busca
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {filteredEmotions.map((em) => {
              const elKey = getElementKey(em.elemento);
              const c = elKey ? elementColor(elKey) : null;
              const headerBg = c ? c.soft : "hsl(var(--secondary) / 0.5)";
              const titleColor = c ? c.text : "hsl(var(--foreground))";

              return (
                <div key={em.id} data-testid={`emotion-card-${em.id}`} className="overflow-hidden rounded-3xl border border-border bg-card">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-6" style={{ backgroundColor: headerBg }}>
                    <div>
                      <h3 className="font-serif text-2xl font-semibold" style={{ color: titleColor }}>{em.emocao}</h3>
                      {em.aliases && em.aliases.length > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          <span className="font-medium text-foreground/75">Estados relacionados:</span> {em.aliases.join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {em.elemento && elKey && (
                        <ElementBadge elementKey={elKey} label={em.elemento} />
                      )}
                      <SourceTag source={{ pdfPage: 12, printedPage: "10" }} />
                    </div>
                  </div>
                  <div className="grid gap-5 p-6 lg:grid-cols-4">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Órgão(s) / Sistema(s) MTC</p>
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        {em.orgaoIds.map((oid) => {
                          const o = getOrgan(oid);
                          if (!o) return null;
                          const organColor = elementColor(o.elemento);
                          return (
                            <Link
                              key={oid}
                              to={`/orgaos/${oid}`}
                              data-testid={`emotion-${em.id}-organ-${oid}`}
                              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs sm:text-sm font-medium transition-transform hover:scale-105"
                              style={{ backgroundColor: organColor.soft, color: organColor.text }}
                            >
                              {o.name} <ArrowRight className="h-3 w-3" />
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Função emocional positiva</p>
                      <p className="mt-2 text-sm leading-relaxed text-foreground">{em.funcaoPositiva}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Em excesso ou bloqueio</p>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{em.excessoBloqueio}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Sinais físicos possíveis</p>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{em.sinaisFisicos}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="rounded-3xl border border-border bg-secondary/50 p-7">
          <h3 className="font-serif text-xl font-medium">Como transformar</h3>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {COMO_TRANSFORMAR.map((t, i) => (
              <div key={i} className="flex gap-2 rounded-xl bg-background/70 px-4 py-3 text-sm text-foreground"><span className="text-gold">•</span> {t}</div>
            ))}
          </div>
        </div>

        <SafetyNote>Este guia é educativo e não substitui acompanhamento com profissionais de saúde. Não adiciona interpretações psicológicas externas.</SafetyNote>
      </section>
    </div>
  );
}
